import React, { useState, useEffect, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { AlertTriangle, MapPin, Camera, Loader, Send } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { HazardReport } from '../types';
import FloatingActionButton from '../components/FloatingActionButton/FloatingActionButton';
import ReportModal from '../components/ReportModal/ReportModal';
import { useReports } from '../hooks/useReports';
import styles from '../styles/Reports.module.scss';

// Lazy load the map component for better performance
const LeafletMap = lazy(() => import('../components/Map/LeafletMap'));

interface ReportFormData {
  title: string;
  hazardType: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  photo?: File;
}

const Reports: React.FC = () => {
  const { currentUser } = useAuth();
  const { reports, loading: reportsLoading, addReport, uploadReportPhoto } = useReports();
  const [localReports, setLocalReports] = useState<HazardReport[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [formData, setFormData] = useState<ReportFormData>({
    title: '',
    hazardType: '',
    description: '',
    severity: 'medium'
  });
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Loading state for future use
  // const [isLoading, setIsLoading] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const hazardTypes = [
    { value: '', label: 'Select Hazard Type' },
    { value: 'tsunami', label: 'Tsunami Warning' },
    { value: 'hurricane', label: 'Hurricane/Typhoon' },
    { value: 'high-waves', label: 'High Waves' },
    { value: 'coastal-flooding', label: 'Coastal Flooding' },
    { value: 'storm-surge', label: 'Storm Surge' },
    { value: 'rip-current', label: 'Dangerous Rip Currents' },
    { value: 'oil-spill', label: 'Oil Spill' },
    { value: 'marine-debris', label: 'Marine Debris' },
    { value: 'earthquake', label: 'Earthquake (Coastal)' },
    { value: 'wildfire', label: 'Coastal Wildfire' },
    { value: 'storm', label: 'Severe Storm' },
    { value: 'other', label: 'Other Hazard' }
  ];

  const severityLevels = [
    { value: 'low', label: 'Low', color: '#3b82f6', description: 'Minor concern, no immediate danger' },
    { value: 'medium', label: 'Medium', color: '#f59e0b', description: 'Moderate risk, caution advised' },
    { value: 'high', label: 'High', color: '#ef4444', description: 'Significant danger, avoid area' },
    { value: 'critical', label: 'Critical', color: '#dc2626', description: 'Extreme danger, evacuate immediately' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Sync local list with real-time reports from Firestore
  useEffect(() => {
    if (!reportsLoading) {
      setLocalReports(reports);
    }
  }, [reports, reportsLoading]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedPhoto(file);
    }
  };

  const handleLocationSelect = (lat: number, lng: number) => {
    setSelectedLocation({ lat, lng });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      toast.error('Please log in to submit a report');
      return;
    }

    if (!selectedLocation) {
      toast.error('Please select a location on the map');
      return;
    }

    setIsSubmitting(true);

    try {
      // Optional: upload photo to Firebase Storage and get URL
      let photoUrl: string | undefined;
      if (selectedPhoto) {
        photoUrl = await uploadReportPhoto(selectedPhoto);
      }

      // Submit to Firestore via hook
      await addReport({
        title: formData.title,
        description: formData.description,
        hazardType: formData.hazardType,
        severity: formData.severity,
        latitude: selectedLocation.lat,
        longitude: selectedLocation.lng,
        photoUrl
      });

      // Reset form
      setFormData({
        title: '',
        hazardType: '',
        description: '',
        severity: 'medium'
      });
      setSelectedPhoto(null);
      setSelectedLocation(null);
      setShowSuccess(true);
      
      // Real-time listener will auto-update; no manual refresh needed
      // Success message is handled by useReports hook
      
      // Hide success message after 3 seconds
      setTimeout(() => setShowSuccess(false), 3000);
      
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error('Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!currentUser) {
    return (
      <div className={styles.reportsPage}>
        <div className={styles.container}>
          <div className={styles.loginPrompt}>
            <AlertTriangle size={48} />
            <h2>Authentication Required</h2>
            <p>Please log in to submit and view hazard reports.</p>
            <button 
              onClick={() => window.location.href = '/login'}
              className={styles.loginButton}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.reportsPage}>
      <div className={styles.container}>
        {/* Header Section */}
        <motion.div
          className={styles.header}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h1 className={styles.title}>
            Report Ocean <span className={styles.titleHighlight}>Hazards</span>
          </h1>
          <p className={styles.subtitle}>
            Help protect your community by reporting ocean hazards in real-time. 
            Click on the map to select a location, then fill out the form.
          </p>
        </motion.div>

        {/* Two-column layout */}
        <div className={styles.mainContent}>
          {/* Left Column - Report Form */}
          <motion.div
            className={styles.formColumn}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >

            <div className={styles.formContainer}>
              <div className={styles.formHeader}>
                <AlertTriangle className={styles.formIcon} />
                <h2>Hazard Report Form</h2>
                {selectedLocation && (
                  <div className={styles.locationIndicator}>
                    <MapPin size={16} />
                    <span>Location selected</span>
                  </div>
                )}
              </div>

              <form onSubmit={handleSubmit} className={styles.reportForm}>
                {/* Report Title */}
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <span className={styles.labelText}>Report Title *</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Brief title describing the hazard"
                    className={styles.input}
                    required
                  />
                </div>

                {/* Hazard Type Selection */}
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <span className={styles.labelText}>Hazard Type *</span>
                  </label>
                  <select
                    name="hazardType"
                    value={formData.hazardType}
                    onChange={handleInputChange}
                    className={styles.select}
                    required
                  >
                    {hazardTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Location Display */}
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <span className={styles.labelText}>Location *</span>
                  </label>
                  <div className={styles.locationDisplay}>
                    {selectedLocation ? (
                      <div className={styles.selectedLocation}>
                        <MapPin size={16} />
                        <span>
                          {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
                        </span>
                      </div>
                    ) : (
                      <div className={styles.noLocation}>
                        <MapPin size={16} />
                        <span>Click on the map to select a location</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Severity Level */}
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <span className={styles.labelText}>Severity Level *</span>
                  </label>
                  <div className={styles.severityGrid}>
                    {severityLevels.map(level => (
                      <motion.label
                        key={level.value}
                        className={`${styles.severityOption} ${formData.severity === level.value ? styles.selected : ''}`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <input
                          type="radio"
                          name="severity"
                          value={level.value}
                          checked={formData.severity === level.value}
                          onChange={handleInputChange}
                          className={styles.radioInput}
                        />
                        <div className={styles.severityContent}>
                          <div className={styles.severityHeader}>
                            <span 
                              className={styles.severityIndicator}
                              style={{ backgroundColor: level.color }}
                            ></span>
                            <span className={styles.severityLabel}>{level.label}</span>
                          </div>
                          <span className={styles.severityDescription}>{level.description}</span>
                        </div>
                      </motion.label>
                    ))}
                  </div>
                </div>

                {/* Description */}
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <span className={styles.labelText}>Description *</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Describe what you observed: wave height, water behavior, timing, affected areas, etc."
                    className={styles.textarea}
                    rows={4}
                    required
                  />
                </div>

                {/* Photo Upload */}
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <span className={styles.labelText}>Photo (Optional)</span>
                  </label>
                  <div className={styles.fileUpload}>
                    <input
                      type="file"
                      onChange={handlePhotoChange}
                      accept="image/*"
                      className={styles.fileInput}
                      id="photo-upload"
                    />
                    <label htmlFor="photo-upload" className={styles.fileLabel}>
                      <Camera size={20} />
                      <span>
                        {selectedPhoto
                          ? selectedPhoto.name
                          : 'Upload a photo of the hazard'
                        }
                      </span>
                    </label>
                  </div>
                </div>


                {/* Submit Button */}
                <motion.button
                  type="submit"
                  className={styles.submitButton}
                  disabled={isSubmitting || !selectedLocation}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isSubmitting ? (
                    <div className={styles.loadingContent}>
                      <Loader className={styles.spinner} size={20} />
                      <span>Submitting Report...</span>
                    </div>
                  ) : (
                    <div className={styles.buttonContent}>
                      <Send size={20} />
                      <span>Submit Hazard Report</span>
                    </div>
                  )}
                </motion.button>
              </form>
            </div>
          </motion.div>

          {/* Right Column - Interactive Map */}
          <motion.div
            className={styles.mapColumn}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <div className={styles.mapContainer}>
              <Suspense fallback={
                <div className={styles.mapLoading}>
                  <motion.div 
                    className={styles.loadingSpinner}
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 12a9 9 0 11-6.219-8.56"/>
                    </svg>
                  </motion.div>
                  <p>Loading interactive map...</p>
                </div>
              }>
                <LeafletMap
                  reports={localReports} // Pass real-time reports to the map
                  onLocationSelect={handleLocationSelect}
                  selectedLocation={selectedLocation}
                />
              </Suspense>
            </div>
          </motion.div>
        </div>

        {/* Success Popup */}
        <AnimatePresence>
          {showSuccess && (
            <motion.div
              className={styles.successOverlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className={styles.successPopup}
                initial={{ opacity: 0, scale: 0.8, y: 50 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: 50 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              >
                <div className={styles.successIcon}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
                    <polyline points="22,4 12,14.01 9,11.01"/>
                  </svg>
                </div>
                <h3 className={styles.successTitle}>Report Submitted Successfully!</h3>
                <p className={styles.successMessage}>
                  Thank you for helping keep our coastal communities safe. 
                  Your report has been added to the map and will be reviewed by our team.
                </p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Floating Action Button */}
        <FloatingActionButton onClick={() => setIsReportModalOpen(true)} />

        {/* Report Modal */}
        <ReportModal
          isOpen={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          onSuccess={() => {
            // Reports will automatically update via real-time listener
          }}
        />
      </div>
    </div>
  );
};

export default Reports;
